import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { Content, ContentList, Schema } from '..';
import { Validation } from '../../../tools';
import { SquidexDate } from '../extensions/squidex-date.extension';
import { AppConfigProvider } from '../../../app-config.provider';
import { Guid } from 'guid-typescript';

const cloneDeep = require('lodash.clonedeep');

export class ContentService {
  private readonly contentBaseUrl: string;
  private readonly cacheClearToken: string;

  constructor(private readonly httpClient: HttpClient,
    private readonly baseUrl: string,
    public readonly schemaName: string,
    private readonly appConfigProvider: AppConfigProvider,
  ) {
    this.contentBaseUrl = `${this.baseUrl}/${schemaName}`;
    this.cacheClearToken = "clearCache=true";
  }

  getAllContent(): Observable<ContentList> {

    const urlParameters: {} = this.filterSoftDelete();

    urlParameters["draftMode"] = true;

    const url = this.contentBaseUrl + "?q=" + encodeURIComponent(JSON.stringify(urlParameters));

    return this.httpClient.get(url) as Observable<ContentList>;
  }

  getAllLanguages(): Observable<any> {
    const languageUrl = this.appConfigProvider.getLanguagePathUrl()

    return this.httpClient.get(languageUrl) as Observable<any>;
  }


  createContent(contentData: any): Observable<Content> {
    if (contentData) {
      const url = `${this.contentBaseUrl}?${this.cacheClearToken}`;
      return this.httpClient.post(url, contentData) as Observable<Content>;
    }
    throw (new Error('data is null when trying to create content'));
  }

  createContentAndPublish(contentData: any): Observable<Content> {
    if (contentData) {
      const url = `${this.contentBaseUrl}?publish=true&${this.cacheClearToken}`;
      return this.httpClient.post(url, contentData) as Observable<Content>;
    }
    throw (new Error('data is null when trying to create and publish content'));
  }

  createDraft(contentId: string): Observable<Content> {
    if (Validation.isValidGuid(contentId)) {
      const url = `${this.contentBaseUrl}/${contentId}/draft`;
      return this.httpClient.post(url, {}) as Observable<Content>;
    } else {
      throw (new Error('Guid is not valid when trying to delete content'));
    }
  }

  deleteDraft(contentId: string): Observable<Content> {
    if (Validation.isValidGuid(contentId)) {
      const url = `${this.contentBaseUrl}/${contentId}/draft`;
      return this.httpClient.delete(url) as Observable<Content>;
    } else {
      throw (new Error('Guid is not valid when trying to delete content'));
    }
  }

  softDeleteContent(contentId: string, draftMode: boolean = false): Observable<Content> {
    if (!Validation.isValidGuid(contentId)) { throw (new Error('Guid is not valid when trying to soft delete content')); }
    return this.getContent(contentId, draftMode).pipe(
      tap(content => {
        var contentSoftDeleteCode = content.data.code.iv + '__' + contentId;
        content.data.deleted = { iv: true };
        content.data.code = { iv: contentSoftDeleteCode };
        content.data.deletedatetime = { iv: `${new SquidexDate(Date.now()).format()}` };
        this.updateContent(contentId, content.data).subscribe(c => {
          return this.archive(c.id);
        })
      })
    );
  }

  deleteContent(contentId: string): Observable<Content> {
    if (Validation.isValidGuid(contentId)) {
      const url = `${this.contentBaseUrl}/${contentId}?${this.cacheClearToken}`;
      return this.httpClient.delete(url) as Observable<Content>;
    } else {
      throw (new Error('Guid is not valid when trying to delete content'));
    }
  }

  private addDraftModeToUrl(url: string, draftMode: boolean) {
    if (draftMode) {
      if (url.indexOf('?') > 0) {
        url += '&draftMode=true ';
      } else {
        url += '?draftMode=true';
      }
    }
    return url;
  }

  private filterSoftDelete() {
    const contentObject =
    {
      filter:
      {
        and:
          [
            {
              path: "data.deleted.iv",
              op: "ne",
              value: true
            }
          ]
      },
      sort:
        [
          {
            path: "data.code.iv",
            order: "ascending"
          }
        ]
    }
    return contentObject;
  }

  getContent(contentId: string, draftMode: boolean = false): Observable<Content> {
    if (Validation.isValidGuid(contentId)) {
      let url = this.addDraftModeToUrl(`${this.contentBaseUrl}/${contentId}`, draftMode);
      return this.httpClient.get(url) as Observable<Content>;
    }
    throw (new Error('Guid is not valid when trying to get content'));
  }

  findContent(oDataQuery: string, draftMode: boolean = false): Observable<ContentList> {
    const url = this.addDraftModeToUrl(`${this.contentBaseUrl}${oDataQuery}`, draftMode);
    return this.httpClient.get(url) as Observable<ContentList>;
  }

  getLastUpdatedContent(): Observable<Content> {
    return this.findContent('?$top=1&$orderby=lastModified desc')
      .pipe(map(contents => {
        if (contents['items']) {
          return contents['items'][0];
        }
        return of(null);
      })) as Observable<Content>;
  }

  getLastPublishedContent(): Observable<Content> {
    return this.findContent('?$top=1&$orderby=data/publishdatetime/iv desc')
      .pipe(map(contents => {
        if (contents['items']) {
          return contents['items'][0];
        }
        return of(null);
      })) as Observable<Content>;
  }

  countContent(): Observable<Number> {
    return this.findContent('?$top=1')
      .pipe(map(content => content['total']));
  }

  getFirstContent(): Observable<Content> {
    return this.findContent('?$top=1')
      .pipe(map(contents => {
        if (contents['items']) {
          return contents['items'][0];
        }
        return of(null);
      })) as Observable<Content>;
  }

  updateContent(contentId: string, contentData: any): Observable<Content> {
    const url = `${this.contentBaseUrl}/${contentId}?${this.cacheClearToken}`;
    return this.httpClient.put(url, contentData) as Observable<Content>;
  }

  updateContentAndStatus(contentId: string, contentData: any, status: string): Observable<Content> {
    const url = `${this.contentBaseUrl}/${contentId}?publish=${(status === "Published" ? "true" : "false")}`;
    return this.httpClient.put(url, contentData) as Observable<Content>;
  }

  getAllContentOrdered(orderBy: string = 'id') {
    const url = `${this.contentBaseUrl}?$orderby=${orderBy}`;
    return this.httpClient.get(url) as Observable<ContentList>;
  }

  updateContentStatus(contentId: string, status: string, dueTime: string): Observable<Content> {
    const url = `${this.contentBaseUrl}/${contentId}/status`;
    const statusData: any = {};
    statusData['status'] = status;
    if (dueTime) {
      statusData['dueTime'] = dueTime;
    }
    return this.httpClient.put(url, statusData) as Observable<Content>;
  }


  createLanguageDefinition(contentData: any): Observable<any> {
    if (contentData) {
      const url = this.appConfigProvider.getSchemaBaseUrl() + '/languages';
      return this.httpClient.post(url, contentData) as Observable<any>;
    }
    throw (new Error('data is null when trying to create content'));
  }

  deleteLanguageDefinition(code: string): Observable<any> {
    if (code) {
      const url = `${this.appConfigProvider.getSchemaBaseUrl()}/languages/${code}`;
      return this.httpClient.delete(url) as Observable<any>;
    }
    throw (new Error('data is null when trying to create content'));
  }

  archive(id: string, date: Date = null) {
    let formattedDate = null;

    if (date) {
      const setDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString()
      formattedDate = setDate.substring(0, 19) + 'Z';
    }

    const archivePayload = {
      status: "Archived",
      dueTime: formattedDate
    }
    this.updateContent(id.toString() + '/status', archivePayload).subscribe(answer => {
      return answer as Content;
    });

  }

}
